import { ChartDataProps, ChartDataPropsTerno, Terno } from "../redux/types"
import warningIcon from '../../assets/icons/warning-icon.svg'
import checkIcon from '../../assets/icons/check-icon.svg'
import errorIcon from '../../assets/icons/error-icon.svg'
import { Dispatch, SetStateAction } from "react"
import { SetState } from "immer/dist/internal"
import { STATUS_TERNO, STATUS_UH, THRESHOLDS_MANUTENCAO } from '../alarmConfig'

const Cargos = {
    Administrador: 1
}

const yellow = '#E5D34A'
const green = '#C5EB7F'
const red = '#F03F3F'

export const ReplaceSpecialCharacters = (text: string) => {
    let newText = text
    newText = newText.replaceAll('+','%2B')
    newText = newText.replaceAll('$','%24')
    newText = newText.replaceAll('&','%26')
    newText = newText.replaceAll(',','%2C')
    newText = newText.replaceAll('/','%2F')
    newText = newText.replaceAll(':','%3A')
    newText = newText.replaceAll(';','%3B')
    newText = newText.replaceAll('=','%3D')
    newText = newText.replaceAll('?','%3F')
    newText = newText.replaceAll('@','%40')

    return newText
}

export const getCargo = (cargo: number) => {
    switch (cargo) {
        case Cargos.Administrador: 
            return "Administrador"
        default:
            return "Other"
    }
}

export const verifyEmail = (email: string) => {
    if(!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)){
        return false
    }
    return true
}

// Posição linear (0-100, com clamp) de um valor dentro de um intervalo [min, max].
const posicaoLinear = (valor: number, min: number, max: number) => {
    if(max <= min) return 0
    return Math.max(0, Math.min(100, ((valor - min) / (max - min)) * 100))
}

// Gradiente CSS de 5 faixas (vermelho/amarelo/verde/amarelo/vermelho) a partir de 4 limites
// já convertidos para posição (0-100) dentro do range exibido no gauge.
const gradiente5Faixas = (p1: number, p2: number, p3: number, p4: number) =>
    `linear-gradient(90deg, #F03F3F ${p1}%, #E5D34A ${p1}%, #E5D34A ${p2}%, #92B258 ${p2}%, ` +
    `#92B258 ${p3}%, #E5D34A ${p3}%, #E5D34A ${p4}%, #F03F3F ${p4}%)`

// Pressão Esquerda/Direita: Pmin/Pmax com tolerância (fatores multiplicativos, ex: 1.06 = +6%).
// < Pmin: vermelho | [Pmin, Pmin*TolSup): amarelo | [Pmin*TolSup, Pmax*TolInf]: verde |
// (Pmax*TolInf, Pmax]: amarelo | > Pmax: vermelho
const getPressaoRange = (pMin: number, pMax: number) => ({ dispMin: pMin * 0.85, dispMax: pMax * 1.15 })

export const getPressurePickup = (pressure: number, minPressure: number, maxPressure: number) => {
    const { dispMin, dispMax } = getPressaoRange(minPressure, maxPressure)
    return posicaoLinear(pressure, dispMin, dispMax)
}

export const getPressaoGradiente = (pMin: number, pMax: number, tolSup: number, tolInf: number) => {
    const { dispMin, dispMax } = getPressaoRange(pMin, pMax)
    return gradiente5Faixas(
        posicaoLinear(pMin, dispMin, dispMax),
        posicaoLinear(pMin * tolSup, dispMin, dispMax),
        posicaoLinear(pMax * tolInf, dispMin, dispMax),
        posicaoLinear(pMax, dispMin, dispMax),
    )
}

// Pressão Média: em torno do setpoint (Pset), com tolerância própria (tolerancia_superior/inferior).
// < Pset*(TolInf-0,01): vermelho | [Pset*(TolInf-0,01), Pset*TolInf): amarelo |
// [Pset*TolInf, Pset*TolSup]: verde | (Pset*TolSup, Pset*(TolSup+0,01)]: amarelo | acima: vermelho
const getPressaoMediaRange = (pset: number, tolSup: number, tolInf: number) =>
    ({ dispMin: pset * (tolInf - 0.03), dispMax: pset * (tolSup + 0.03) })

export const getPressaoMediaPickup = (pressaoMedia: number, pset: number, tolSup: number, tolInf: number) => {
    const { dispMin, dispMax } = getPressaoMediaRange(pset, tolSup, tolInf)
    return posicaoLinear(pressaoMedia, dispMin, dispMax)
}

export const getPressaoMediaGradiente = (pset: number, tolSup: number, tolInf: number) => {
    const { dispMin, dispMax } = getPressaoMediaRange(pset, tolSup, tolInf)
    return gradiente5Faixas(
        posicaoLinear(pset * (tolInf - 0.01), dispMin, dispMax),
        posicaoLinear(pset * tolInf, dispMin, dispMax),
        posicaoLinear(pset * tolSup, dispMin, dispMax),
        posicaoLinear(pset * (tolSup + 0.01), dispMin, dispMax),
    )
}

export const getDeslocamento = (item: Terno) => {
    return Math.abs(item.transmissor_posic_direito - item.transmissor_posic_esquerdo )
}

// Diferença de deslocamento: verde abaixo de DeltaZ, amarelo entre DeltaZ e a altura de terno
// crítico, vermelho acima dela. Reaproveita o mesmo gauge (verde 0-40% / amarelo 40-60% /
// vermelho 60-100%) usado por temperatura/filtro.
export const getDeslocamentoPickup = (item: Terno) => {
    const deslocamento = getDeslocamento(item)
    const deltaZ = item.diferenca_altura_correcao ?? 0
    const alturaCritico = item.altura_terno_critico ?? item.zmax
    return mapValorParaPickup(deslocamento, deltaZ, alturaCritico, alturaCritico * 1.5)
}

export const getPressaoMedia = (pressao1: number, pressao2: number) => {
    return (pressao1 + pressao2)/2
}

// Mapeia um valor de sensor para posição (0-100) no gauge, alinhando os limites de
// atenção/crítico às faixas de cor do gradiente (verde 0-40%, amarelo 40-60%, vermelho 60-100%).
const mapValorParaPickup = (valor: number, atencao: number, critico: number, max: number) => {
    if(valor <= 0) return 0
    if(valor <= atencao) return (valor / atencao) * 40
    if(valor <= critico) return 40 + ((valor - atencao) / (critico - atencao)) * 20
    if(valor <= max) return 60 + ((valor - critico) / (max - critico)) * 40
    return 100
}

export const getTemperaturePickup = (temperature: number) => {
    const { atencao, critico } = THRESHOLDS_MANUTENCAO.temperatura
    return mapValorParaPickup(temperature, atencao, critico, 100)
}

// Escala do gauge de saturação do filtro (bar) vai até 1,5x o valor crítico.
export const getFiltroPickup = (saturacao: number) => {
    const { atencao, critico } = THRESHOLDS_MANUTENCAO.filtro
    return mapValorParaPickup(saturacao, atencao, critico, critico * 1.5)
}

export const getSaturacaoStatus = (saturation: number) => {
    return (saturation / 5) * 100
}

// Autoteste da bomba (psi): 0-4000 vermelho, 4000-4400 amarelo, 4400-5200 verde, acima vermelho.
// Escala do gauge vai de 0 a 6000 (headroom acima do limite superior).
const AUTOTESTE_ESCALA_MAX = 6000
export const AUTOTESTE_LIMITES = { baixo: 4000, atencaoAlto: 4400, alto: 5200 }

export const getAutotestePickup = (pressao: number) => {
    return posicaoLinear(pressao, 0, AUTOTESTE_ESCALA_MAX)
}


export const checkPasswordRestrictions = (text:string) => {


    const getMatch = (regex: RegExp) => {

      const value = text.match(regex)
      return value == null? 0:value.length

    }

      const getLength = () => {
            return text.length >= 12 ? true:false
      }
      const getLowercase = () => {
          return getMatch(/[a-z]/g) > 0
      }

      const getUppercase = () => {
            return getMatch(/[A-Z]/g) > 0
      }

      const getSpecial = () => {
          return getMatch(/[!@#$%ˆ&*()-+~`_={}[\]|\\;:'"<>,.?/]/g) > 0
      }

      const verify = {lenght: getLength(), lowercase: getLowercase(), uppercase: getUppercase(),
                     special: getSpecial()}

      return verify
}

export const getColors = (index: number) => {
    switch(index){
        case 0:
            return ""
    }
}

export const AdjustObs = (obs: string) => {
    const newObs = obs.split(":")
    const observacao = newObs[3]
    const terno = newObs[2]
    const lado = newObs[1]
    let manual 
    let valvula
    if(newObs[0].includes('Manual')){
        valvula = newObs[0].split(' - ')[0]
        manual  = newObs[0].split(' - ')[1]
    }else{
        valvula = newObs[0]
    }

    return {valvula, lado, terno, observacao, manual}
}

export const checkCredentials = (email: string, password: string) => {
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email) || password.length < 12){
        if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email) && password.length < 12){
            return {email: false,password: false}
        }else if(password.length < 12){
            return {email: false,password: true}
        }else{
            return {email: true,password: false}
        }
    }else{
        return {email: true,password: true}
    }
}

const COR_HEX: Record<string, string> = {
    verde:    green,
    amarelo:  yellow,
    vermelho: red,
}

const COR_ICON: Record<string, string> = {
    verde:    checkIcon,
    amarelo:  warningIcon,
    vermelho: errorIcon,
}

export const getStatusTerno = (status: number) => {
    const entrada = STATUS_TERNO[status]
    if (!entrada) return { msg: 'Erro ao encontrar status', color: red, icon: errorIcon }
    return { msg: entrada.descricao, color: COR_HEX[entrada.cor], icon: COR_ICON[entrada.cor] }
}

export const getStatusUH = (status: number) => {
    const entrada = STATUS_UH[status]
    if (!entrada) return { msg: 'Status desconhecido', color: yellow, icon: warningIcon }
    return { msg: entrada.descricao, color: COR_HEX[entrada.cor], icon: COR_ICON[entrada.cor] }
}

export const getTimestamps = (date: Date, time: Date) => {
    let day:number|string = date.getDate()

    if(day < 10){
        day = "0"+day
    }

    let month

    if(date.getMonth() + 1 < 10){
        month = "0"+(date.getMonth() + 1)
    }
    else{
        month = date.getMonth() + 1
    }

    const year = date.getFullYear()

    let hour:number|string = time.getHours()

    if(hour < 10){
        hour = "0"+hour
    }

    let minutes:number|string = time.getMinutes()

    if(minutes < 10){
        minutes = "0"+minutes
    }

    return year+"-"+month+"-"+day+"T"+hour+":"+minutes+":00.0000"
}

export const adjustTime = (time: number) => {
    if(time < 10){
        return "0:0"+time
    }
    if(time < 60){
        return "0:"+time
    }
}

export const ResolveErrors = (status: number, code: string, message: string, navigate: any, dispatch: any, 
    content?: any) => {
switch(status){
    case 400:
        if(code === 'LGN-1'){
            dispatch({type: 'ADD_NOTIFICATION', status: 3, title: '', text: message})
            return
        }
        if(code === 'LGN-3'){
            dispatch({type: 'ADD_NOTIFICATION', status: 3, title: '', text: message})
            return
        }   
        if(code === 'ACS-11'){
            dispatch({type: 'ADD_NOTIFICATION', status: 2, title: '', text: message+", você será redirecionado para o Conecta para prosseguir com a operação", link: true})
            return
        }
        if(code === 'DSS-1'){
            dispatch({type: 'ADD_NOTIFICATION', status: 2, title: '', text: message})
            // navigate('/privacidade-empresa', {state: {nivel: 1, nome: content.nomeUsuario, filial: content.nomeFilial,
            //     idFilial: content.idFilial}})
            return
        }
        if(code === 'DSS-2'){
            dispatch({type: 'ADD_NOTIFICATION', status: 2, title: '', text: message+", você será redirecionado para o Conecta para prosseguir com a operação", link: true})
            return
        }
        dispatch({type: 'ADD_NOTIFICATION', status: 3, title: '', text: message})
        break;
    case 401:
        if(code === 'ACS-9'){
            localStorage.setItem("@logged", "")
            navigate('/')
        }
        if(code === 'ACS-10'){            
            localStorage.setItem("@logged", "")
            navigate('/')
        }
        dispatch({type: 'ADD_NOTIFICATION', status: 3, title: '', text: message})
        break; 
    case 403:
        if(code === 'ACS-4'){
            dispatch({type: 'ADD_NOTIFICATION', status: 2, title: '', text: message+", você será redirecionado para o Conecta para prosseguir com a operação", link: true})
            return
        }
        // if(code === 'ACS-6'){
        //     dispatch({type: 'ADD_NOTIFICATION', status: 2, title: '', text: message})
        //     navigate('/autenticacao')
        //     return
        // }
        // if(code === 'ACS-7'){
        //     dispatch({type: 'ADD_NOTIFICATION', status: 2, title: '', text: message})
        //     navigate('/autenticacao')
        //     return
        // }
        // dispatch({type: 'ADD_NOTIFICATION', status: 3, title: '', text: message})
        break; 
    case 500:
        dispatch({type: 'ADD_NOTIFICATION', status: 3, title: '', text: "Internal server error"})
        break  
    default:
        dispatch({type: 'ADD_NOTIFICATION', status: 3, title: "", text: message})
        break   
}       
}

const getDate = (date: Date) => {
    const day = date.getDate()
    let month
    if(date.getMonth() + 1 < 10){
        month = "0"+(date.getMonth() + 1)
    }
    else{
         month = date.getMonth() + 1
    }
    const year = date.getFullYear()
 
    const getHour = () => {
        let value = ''
        if(date.getHours() < 10) {
            value = '0'+date.getHours()+":"
        }else{
            value = date.getHours()+":"
        }
        if(date.getMinutes() < 10){
            value = value + "0"+date.getMinutes()
        }else{
            value = value + date.getMinutes()
        }
        return value
    }

    return day+"/"+month+"/"+year+" "+getHour()
    
}


export const getChartDataGen = (response: any, content: string) => {
    let dataToReturn: ChartDataProps = []
    let countTerno = 1
    let min = 100000
    let max = -10000

    const getConfig = (arr: any) => {
        const timestamps = [];
        const valores = [];

        for (const objeto of arr) {
            timestamps.push(getDate(new Date(objeto.timestamp)));
            valores.push(objeto.value);
        }

        return {timestamps, valores}
    }

    const getConfigCritico = (arr: any) => {
        const timestamps = [];
        const valores = [];

        for (const objeto of arr) {
            timestamps.push(getDate(new Date(objeto.timestamp)));
            valores.push(objeto.totalCount);
        }

        return {timestamps, valores}
    }

    const adjustDataCritico = (arr: any, label: string) => {
        const {timestamps, valores} = getConfigCritico(arr)
        min = valores[0]
        max =  valores[0]
        for(let i = 0; i < valores.length; i++) {
            if(min > valores[i]){min = valores[i]}
            if(max < valores[i]){min = valores[i]}
        }
        return  {
            labels: timestamps,
            datasets: [{
                label: label,
                data: valores,
                fill: false,
                backgroundColor: 'rgba(134, 65, 244, 0.8)',
                borderColor: 'rgba(134, 65, 244, 0.8)'
            }]}
    } 

    const adjustDataTempoNivelamento = (arr: any, label: string) => {

        const timestamps = [];
        const valoresDeslocamento = [];
        const valoresToleranciaDeslocamento = [];

        for (const objeto of arr) {
            timestamps.push(getDate(new Date(objeto.timestamp)));
            valoresDeslocamento.push(objeto.avgAbsDifference);
            valoresToleranciaDeslocamento.push(objeto.avgAlturaCorrecao);
        }

        min = valoresDeslocamento[0]
        max =  valoresDeslocamento[0]
        for(let i = 0; i < valoresDeslocamento.length; i++) {
            if(min > valoresDeslocamento[i]){min = valoresDeslocamento[i]}
            if(max < valoresDeslocamento[i]){min = valoresDeslocamento[i]}
        }
        return  {
            labels: timestamps,
            datasets: [{
                label: 'Diferença de deslocamento entre lados (mm)',
                data: valoresDeslocamento,
                fill: false,
                backgroundColor: 'rgba(134, 65, 244, 0.8)',
                borderColor: 'rgba(134, 65, 244, 0.8)'
            },
            {
                label: 'Diferença de altura para correção (mm)',
                data: valoresToleranciaDeslocamento,
                fill: false,
                backgroundColor: 'rgba(255, 87, 51, 0.8)',
                borderColor: 'rgba(255, 87, 51, 0.8)'
            },
            ]}
    } 

    const adjustDataMediaStatus = (arr: any, label: string) => {

        const timestamps = [];
        const valoresMedia = []

        for (const objeto of arr) {
            timestamps.push(getDate(new Date(objeto.timestamp)));
            valoresMedia.push(objeto.status);      
        }

        min = valoresMedia[0]
        max =  valoresMedia[0]
        for(let i = 0; i < valoresMedia.length; i++) {
            if(min > valoresMedia[i]){min = valoresMedia[i]}
            if(max < valoresMedia[i]){min = valoresMedia[i]}
        }

        return  {
            labels: timestamps,
            datasets: [{
                label: 'Média do status do terno',
                data: valoresMedia,
                fill: false,
                backgroundColor: 'rgba(134, 65, 244, 0.8)',
                borderColor: 'rgba(134, 65, 244, 0.8)'
            }
        ]}
    } 

    const adjustData = (arr: any, label: string) => {
        const {timestamps, valores} = getConfig(arr)
        min = valores[0]
        max =  valores[0]
        for(let i = 0; i < valores.length; i++) {
            if(min > valores[i]){min = valores[i]}
            if(max < valores[i]){min = valores[i]}
        }
        return  {
            labels: timestamps,
            datasets: [{
                label: label,
                data: valores,
                fill: false,
                backgroundColor: 'rgba(134, 65, 244, 0.8)',
                borderColor: 'rgba(134, 65, 244, 0.8)'
            }]}
    } 
    
    switch(content){
        case 'temperatura-oleo': 
            dataToReturn.push({name: 'Temperatura óleo', config: adjustData(response, 'Temperatura Óleo (ºc)'), ranges: {min: min, max: max}})     
            break
        case 'umidade-oleo':
            dataToReturn.push({name: 'Umidade óleo', config: adjustData(response, 'Umidade Óleo (%)'), ranges: {min: min, max: max}})
            break
        case 'pressao-filtro':
            dataToReturn.push({name: 'Pressão filtro', config: adjustData(response, 'Pressão filtro (psi)'), ranges: {min: min, max: max}})
            break
        case 'terno-critico':
            for(let i = 0; i < response.length; i++){
                dataToReturn.push({name: 'Terno '+response[i].terno_id, config: adjustDataCritico(response[i].data, 'Ocorrências de bit de terno crítico (un.)'), ranges: {min: min, max: max}})
                countTerno++
            }
            break
        case 'tempo-nivelamento':
            for(let i = 0; i < response.length; i++){
                dataToReturn.push({name: 'Terno '+response[i].terno_id, config: adjustDataTempoNivelamento(response[i].data, ''), ranges: {min: min, max: max}})
                countTerno++
            }
            break
        case 'media-status-terno':
            for(let i = 0; i < response.length; i++){
                dataToReturn.push({name: 'Terno '+response[i].terno_id, config: adjustDataMediaStatus(response[i].data, 'Média do status do terno'), ranges: {min: 0, max: 37}})
                countTerno++
            }
            break
    }

    return dataToReturn

}

export const getChartData = (response: any, content: string) => {
    let dataToReturn: ChartDataProps = []
    let countTerno = 1
    let min = 100000
    let max = -10000

    const getConfig = (arr: any) => {
        const timestamps = [];
        const valores = [];

        for (const objeto of arr) {
            timestamps.push(getDate(new Date(objeto.timestamp)));
            valores.push(objeto.value);
        }

        return {timestamps, valores}
    }

    const getConfigCritico = (arr: any) => {
        const timestamps = [];
        const valores = [];

        for (const objeto of arr) {
            timestamps.push(getDate(new Date(objeto.timestamp)));
            valores.push(objeto.totalCount);
        }

        return {timestamps, valores}
    }

    const adjustDataCritico = (arr: any, label: string) => {
        const {timestamps, valores} = getConfigCritico(arr)
        min = valores[0]
        max =  valores[0]
        for(let i = 0; i < valores.length; i++) {
            if(min > valores[i]){min = valores[i]}
            if(max < valores[i]){min = valores[i]}
        }
        return  {
            labels: timestamps,
            datasets: [{
                label: label,
                data: valores,
                fill: false,
                backgroundColor: 'rgba(134, 65, 244, 0.8)',
                borderColor: 'rgba(134, 65, 244, 0.8)'
            }]}
    } 

    const adjustData = (arr: any, label: string) => {
        const {timestamps, valores} = getConfig(arr)
        min = valores[0]
        max =  valores[0]
        for(let i = 0; i < valores.length; i++) {
            if(min > valores[i]){min = valores[i]}
            if(max < valores[i]){min = valores[i]}
        }
        return  {
            labels: timestamps,
            datasets: [{
                label: label,
                data: valores,
                fill: false,
                backgroundColor: 'rgba(134, 65, 244, 0.8)',
                borderColor: 'rgba(134, 65, 244, 0.8)'
            }]}
    } 
    
    switch(content){
        case 'autoteste':
            if(response.unidade_hidraulica_2){
                dataToReturn.push({name: 'Teste bomba 1 - UH1', config: adjustData(response.unidade_hidraulica_1.teste_bomba_1, 'Bomba 1'), ranges: {min: min, max: max}})
                dataToReturn.push({name: 'Teste bomba 2 - UH1', config: adjustData(response.unidade_hidraulica_1.teste_bomba_2, 'Bomba 2'), ranges: {min: min, max: max}})
                dataToReturn.push({name: 'Teste bomba 1 - UH2', config: adjustData(response.unidade_hidraulica_2.teste_bomba_1, 'Bomba 1'), ranges: {min: min, max: max}})
                dataToReturn.push({name: 'Teste bomba 2 - UH2', config: adjustData(response.unidade_hidraulica_2.teste_bomba_2, 'Bomba 2'), ranges: {min: min, max: max}})
            }else{
                dataToReturn.push({name: 'Teste bomba 1', config: adjustData(response.unidade_hidraulica_1.teste_bomba_1, 'Bomba 1'), ranges: {min: min, max: max}})
                dataToReturn.push({name: 'Teste bomba 2', config: adjustData(response.unidade_hidraulica_1.teste_bomba_2, 'Bomba 2'), ranges: {min: min, max: max}})
            }
            break
        case 'temperatura-oleo': 
            if(response.unidade_hidraulica_2){
                dataToReturn.push({name: 'Temperatura óleo - UH1', config: adjustData(response.unidade_hidraulica_1, 'Temperatura Óleo'), ranges: {min: min, max: max}})
                dataToReturn.push({name: 'Temperatura óleo - UH2', config: adjustData(response.unidade_hidraulica_2, 'Temperatura Óleo'), ranges: {min: min, max: max}})
            }else{
                dataToReturn.push({name: 'Temperatura óleo', config: adjustData(response.unidade_hidraulica_1, 'Temperatura Óleo'), ranges: {min: min, max: max}})
            }
            break
        case 'umidade-oleo':
            if(response.unidade_hidraulica_2){
                dataToReturn.push({name: 'Umidade óleo - UH1', config: adjustData(response.unidade_hidraulica_1, 'Umidade Óleo'), ranges: {min: min, max: max}})
                dataToReturn.push({name: 'Umidade óleo - UH2', config: adjustData(response.unidade_hidraulica_2, 'Umidade Óleo'), ranges: {min: min, max: max}})
            }else{
                dataToReturn.push({name: 'Umidade óleo', config: adjustData(response.unidade_hidraulica_1, 'Umidade Óleo'), ranges: {min: min, max: max}})
            }
            break
        case 'pressao-filtro':
            dataToReturn.push({name: 'Pressão filtro 1', config: adjustData(response.filtro_bomba_1, 'Pressão filtro'), ranges: {min: min, max: max}})
            dataToReturn.push({name: 'Pressão filtro 2', config: adjustData(response.filtro_bomba_2, 'Pressão filtro'), ranges: {min: min, max: max}})
            break
        case 'terno-critico':
            for(let i = 0; i < response.length; i++){
                dataToReturn.push({name: 'Terno '+response[i].terno_id, config: adjustDataCritico(response[i].data,'Terno '+response[i].terno_id), ranges: {min: min, max: max}})
            }
            break
    }

    return dataToReturn

}

export const getDateRange = (time: Date, date: Date) => {
    const day = date.getDate()
    let month
    if(date.getMonth() + 1 < 10){
        month = "0"+(date.getMonth() + 1)
    }
    else{
            month = date.getMonth() + 1
    }
    const year = date.getFullYear()
    let hour
    if(time.getHours() < 10){
        hour = "0"+(time.getHours())
    }
    else{
        hour = time.getHours() 
    }
    let minutes
    if(time.getMinutes() < 10){
        minutes = "0"+(time.getMinutes() )
    }
    else{
        minutes = time.getMinutes()
    }

    return year+"-"+month+"-"+day+"T"+hour+":"+minutes+":00.000Z"
    
}

export const getIsoString = (year: string, month: string, day: string, hour: string) => {
    return year+"-"+month+"-"+day+"T"+hour+":00.000Z"
}